/**
 * Book type as a capability profile — one spine, different labels and workbench views.
 * Stored in book.metadata.bookType (+ metadata.structure for articles).
 */

export const BOOK_TYPES = [
  "fiction",
  "instructional",
  "historical",
  "childrens",
  "article",
] as const;

export type BookTypeId = (typeof BOOK_TYPES)[number];

export const BOOK_STRUCTURES = ["chapters", "single", "collection", "spreads"] as const;
export type BookStructure = (typeof BOOK_STRUCTURES)[number];

export const CITATION_STYLES = ["apa", "mla", "chicago"] as const;
export type CitationStyle = (typeof CITATION_STYLES)[number];

export type WorkbenchViewId =
  | "book"
  | "planning"
  | "plot"
  | "codex"
  | "characters"
  | "locations"
  | "timeline"
  | "themes"
  | "research"
  | "formatter"
  | "audio"
  | "beta"
  | "adminFeedback"
  | "settings";

export type EditorTabId =
  | "manuscript"
  | "summary"
  | "storyCraft"
  | "history"
  | "characters"
  | "locations"
  | "timeline"
  | "themesMotifs";

export type BookTypeProfile = {
  id: BookTypeId;
  label: string;
  description: string;
  /** Display name for body spine items (Chapter / Section / Article / Page). */
  bodyLabel: string;
  bodyLabelPlural: string;
  numberBodyItems: boolean;
  /** False for a single-article project — still show matter (bibliography). */
  allowMultipleBodyItems: boolean;
  showSpineList: boolean;
  research: boolean;
  citations: boolean;
  inlineImages: boolean;
  citationStyle: CitationStyle | null;
  structure: BookStructure;
  views: readonly WorkbenchViewId[];
  editorTabs: readonly EditorTabId[];
};

const ALWAYS_VIEWS: WorkbenchViewId[] = ["book", "formatter", "audio", "beta", "settings"];

const FICTION_TABS: EditorTabId[] = [
  "manuscript",
  "summary",
  "storyCraft",
  "history",
  "characters",
  "locations",
  "timeline",
  "themesMotifs",
];

const BRIEF_HISTORY_TABS: EditorTabId[] = ["manuscript", "summary", "history"];
const CHILDRENS_TABS: EditorTabId[] = ["manuscript", "summary", "history", "characters"];
const HISTORICAL_TABS: EditorTabId[] = ["manuscript", "summary", "history", "timeline"];

export const BOOK_TYPE_LABELS: Record<BookTypeId, string> = {
  fiction: "Fiction",
  instructional: "Motivational / instructional",
  historical: "Historical non-fiction",
  childrens: "Children's book",
  article: "Article",
};

export const BOOK_TYPE_DESCRIPTIONS: Record<BookTypeId, string> = {
  fiction: "Novels and stories with chapters, characters, and plot tools.",
  instructional: "How-to and motivational books with sections, research, and citations.",
  historical: "Documented non-fiction with a timeline, research library, and citations.",
  childrens: "Illustrated pages with optional character notes.",
  article: "A single article or a collection — no chapter numbers.",
};

function withAlways(views: WorkbenchViewId[]): WorkbenchViewId[] {
  const set = new Set<WorkbenchViewId>([...views, ...ALWAYS_VIEWS, "research"]);
  const order: WorkbenchViewId[] = [
    "book",
    "planning",
    "plot",
    "codex",
    "characters",
    "locations",
    "timeline",
    "themes",
    "research",
    "formatter",
    "audio",
    "beta",
    "settings",
  ];
  return order.filter((v) => set.has(v));
}

export function parseBookType(value: unknown): BookTypeId {
  if (typeof value === "string" && (BOOK_TYPES as readonly string[]).includes(value)) {
    return value as BookTypeId;
  }
  return "fiction";
}

export function parseBookStructure(value: unknown, bookType: BookTypeId): BookStructure {
  if (typeof value === "string" && (BOOK_STRUCTURES as readonly string[]).includes(value)) {
    return value as BookStructure;
  }
  if (bookType === "article") return "single";
  if (bookType === "childrens") return "chapters";
  return "chapters";
}

export function parseCitationStyle(value: unknown): CitationStyle | null {
  if (typeof value === "string" && (CITATION_STYLES as readonly string[]).includes(value)) {
    return value as CitationStyle;
  }
  return null;
}

export function resolveBookTypeProfile(
  bookTypeRaw?: unknown,
  structureRaw?: unknown
): BookTypeProfile {
  const id = parseBookType(bookTypeRaw);
  const structure = parseBookStructure(structureRaw, id);
  return bookTypeProfile(id, structure);
}

export function profileFromBookMetadata(
  metadata: Record<string, unknown> | null | undefined
): BookTypeProfile {
  return resolveBookTypeProfile(metadata?.bookType, metadata?.structure);
}

export function bookTypeProfile(id: BookTypeId, structure?: BookStructure): BookTypeProfile {
  const resolvedStructure = structure ?? parseBookStructure(undefined, id);
  switch (id) {
    case "instructional":
      return {
        id,
        label: BOOK_TYPE_LABELS[id],
        description: BOOK_TYPE_DESCRIPTIONS[id],
        bodyLabel: "Section",
        bodyLabelPlural: "Sections",
        numberBodyItems: true,
        allowMultipleBodyItems: true,
        showSpineList: true,
        research: true,
        citations: true,
        inlineImages: true,
        citationStyle: "apa",
        structure: "chapters",
        views: withAlways(["planning"]),
        editorTabs: BRIEF_HISTORY_TABS,
      };
    case "historical":
      return {
        id,
        label: BOOK_TYPE_LABELS[id],
        description: BOOK_TYPE_DESCRIPTIONS[id],
        bodyLabel: "Chapter",
        bodyLabelPlural: "Chapters",
        numberBodyItems: true,
        allowMultipleBodyItems: true,
        showSpineList: true,
        research: true,
        citations: true,
        inlineImages: false,
        citationStyle: "apa",
        structure: "chapters",
        views: withAlways(["planning", "timeline"]),
        editorTabs: HISTORICAL_TABS,
      };
    case "childrens":
      return {
        id,
        label: BOOK_TYPE_LABELS[id],
        description: BOOK_TYPE_DESCRIPTIONS[id],
        bodyLabel: "Page",
        bodyLabelPlural: "Pages",
        numberBodyItems: true,
        allowMultipleBodyItems: true,
        showSpineList: true,
        research: true,
        citations: false,
        inlineImages: true,
        citationStyle: null,
        structure: resolvedStructure === "spreads" ? "spreads" : "chapters",
        views: withAlways(["planning", "characters"]),
        editorTabs: CHILDRENS_TABS,
      };
    case "article": {
      const isCollection = resolvedStructure === "collection";
      return {
        id,
        label: BOOK_TYPE_LABELS[id],
        description: BOOK_TYPE_DESCRIPTIONS[id],
        bodyLabel: "Article",
        bodyLabelPlural: "Articles",
        numberBodyItems: false,
        allowMultipleBodyItems: isCollection,
        showSpineList: true,
        research: true,
        citations: true,
        inlineImages: false,
        citationStyle: "apa",
        structure: isCollection ? "collection" : "single",
        views: withAlways(["planning"]),
        editorTabs: BRIEF_HISTORY_TABS,
      };
    }
    default:
      return {
        id: "fiction",
        label: BOOK_TYPE_LABELS.fiction,
        description: BOOK_TYPE_DESCRIPTIONS.fiction,
        bodyLabel: "Chapter",
        bodyLabelPlural: "Chapters",
        numberBodyItems: true,
        allowMultipleBodyItems: true,
        showSpineList: true,
        research: true,
        citations: false,
        inlineImages: false,
        citationStyle: null,
        structure: "chapters",
        views: withAlways(["planning", "plot", "codex", "characters", "locations", "timeline", "themes"]),
        editorTabs: FICTION_TABS,
      };
  }
}

export function defaultBodyTitle(profile: BookTypeProfile, order: number): string {
  if (!profile.numberBodyItems) return `Untitled ${profile.bodyLabel}`;
  return `${profile.bodyLabel} ${order}`;
}

export function bibliographyTitleForStyle(style: CitationStyle | null | undefined): string {
  if (style === "mla") return "Works Cited";
  if (style === "chicago") return "Bibliography";
  return "References";
}

export function isEditorTabEnabled(profile: BookTypeProfile, tab: EditorTabId): boolean {
  return profile.editorTabs.includes(tab);
}

export function isWorkbenchViewEnabled(profile: BookTypeProfile, view: WorkbenchViewId): boolean {
  return profile.views.includes(view) || view === "adminFeedback";
}

export type CreateBookOptions = {
  bookType?: BookTypeId;
  structure?: BookStructure;
};

export function metadataForNewBook(profile: BookTypeProfile): Record<string, unknown> {
  return {
    language: "en",
    keywords: [],
    bookType: profile.id,
    structure: profile.structure,
    ...(profile.citationStyle ? { citationStyle: profile.citationStyle } : {}),
  };
}
